import { useQuery } from '@tanstack/react-query';
import { type ColumnDef } from '@tanstack/react-table';
import {
  Activity,
  AlertTriangle,
  Download,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Server,
  Trash2,
  Wrench,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { DashboardLayout } from '@/layout/DashboardLayout';
import {
  Button,
  ConfirmDialog,
  Dialog,
  Input,
  ManagedDataTable,
  StatisticCard,
} from '@/design-system';
import {
  managerConnectorService,
  type ManagerConnectorGateway,
  type ManagerConnectorOverview,
} from './api/connector-gateways.service';
import { InstallerReleasesModal } from './InstallerReleasesModal';
import { RepairPairingDialog } from './RepairPairingDialog';
import { useAuth } from '@/core/auth';
import { resolveError } from '@/core/errors/resolveError';

type DialogState =
  | { kind: 'none' }
  | { kind: 'delete'; gateway: ManagerConnectorGateway }
  | { kind: 'target-version'; gateway: ManagerConnectorGateway };

export function ConnectorGatewaysPage() {
  const { user } = useAuth();
  const canRepair = user?.role === 'SUPERADMIN' || user?.role === 'SUPPORT';
  const [repairId, setRepairId] = useState<string | null>(null);
  const [operationError, setOperationError] = useState('');
  const [dialog, setDialog] = useState<DialogState>({ kind: 'none' });
  const [targetVersionValue, setTargetVersionValue] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [releasesOpen, setReleasesOpen] = useState(false);

  const overview = useQuery<ManagerConnectorOverview>({
    queryKey: ['manager-connector-overview'],
    queryFn: () => managerConnectorService.overview(),
    refetchInterval: 30_000,
  });

  const gateways = useQuery<ManagerConnectorGateway[]>({
    queryKey: ['manager-connector-gateways'],
    queryFn: () => managerConnectorService.listGateways(),
    refetchInterval: 20_000,
  });

  const overviewData = overview.data ?? {
    totalGateways: 0,
    activeGateways: 0,
    offlineGateways: 0,
    totalDlqPending: 0,
    gatewaysByAdapter: [],
    topLaggingTenants: [],
  };

  const errorMessage = overview.error
    ? (overview.error as Error).message
    : gateways.error
      ? (gateways.error as Error).message
      : null;

  async function toggleEnabled(g: ManagerConnectorGateway) {
    setIsSubmitting(true);
    try {
      await managerConnectorService.setEnabled(g.id, !g.enabled);
      await gateways.refetch();
      await overview.refetch();
    } catch (err) {
      setOperationError(resolveError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (dialog.kind !== 'delete') return;
    setOperationError('');
    setIsSubmitting(true);
    try {
      await managerConnectorService.deleteGateway(dialog.gateway.id);
      setDialog({ kind: 'none' });
      await gateways.refetch();
      await overview.refetch();
    } catch (err) {
      setOperationError(resolveError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmTargetVersion() {
    if (dialog.kind !== 'target-version') return;
    setIsSubmitting(true);
    try {
      const value = targetVersionValue.trim();
      await managerConnectorService.setTargetVersion(dialog.gateway.id, value || null);
      setDialog({ kind: 'none' });
      setTargetVersionValue('');
      await gateways.refetch();
    } finally {
      setIsSubmitting(false);
    }
  }

  const columns = useMemo<ColumnDef<ManagerConnectorGateway, unknown>[]>(
    () => [
      {
        accessorKey: 'companyName',
        header: 'Tenant',
        size: 180,
        cell: ({ row }) => (
          <div className="w-40 min-w-0" title={row.original.companyName}>
            <div className="truncate font-medium text-gray-900">{row.original.companyName}</div>
            <div className="truncate font-mono text-xs text-gray-400">{row.original.companyId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Gateway',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-gray-900">{row.original.name}</div>
            <div className="truncate text-xs text-gray-500">{row.original.integrationId}</div>
          </div>
        ),
      },
      {
        accessorKey: 'adapterId',
        filterFn: 'equalsString',
        header: 'Adapter',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate text-gray-700">{row.original.adapterId}</div>
            <div className="truncate font-mono text-xs text-gray-400">
              {row.original.adapterVersion ?? '—'}
              {row.original.targetVersion ? ` → ${row.original.targetVersion}` : ''}
            </div>
          </div>
        ),
      },
      {
        // P3.1 (issue #62): versão do manifest JSON ativo no sidecar. Quando
        // null, sidecar está em modo legacy (regras hardcoded no binário).
        id: 'manifestVersion',
        header: 'Manifest',
        cell: ({ row }) => (
          <div className="min-w-0">
            <div className="truncate font-mono text-xs text-gray-700">
              {row.original.manifestVersion ?? (
                <span className="text-gray-400">legacy</span>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'effectiveStatus',
        filterFn: 'equalsString',
        header: 'Status',
        cell: ({ row }) => {
          const eff = row.original.effectiveStatus;
          const tone =
            eff === 'active'
              ? 'bg-emerald-100 text-emerald-700'
              : eff === 'paused'
                ? 'bg-amber-100 text-amber-800'
                : eff === 'offline'
                  ? 'bg-red-100 text-red-700'
                  : eff === 'pairing'
                    ? 'bg-blue-100 text-blue-700'
                    : eff === 'error'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-gray-100 text-gray-600';
          return (
            <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
              {eff}
            </span>
          );
        },
      },
      {
        accessorKey: 'releaseChannel',
        filterFn: 'equalsString',
        header: 'Canal',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-gray-600">{row.original.releaseChannel}</span>
        ),
      },
      {
        accessorKey: 'lastSeenAt',
        header: 'Última visão',
        cell: ({ row }) =>
          row.original.lastSeenAt
            ? new Date(row.original.lastSeenAt).toLocaleString('pt-BR')
            : <span className="text-gray-400">—</span>,
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Ações</div>,
        cell: ({ row }) => {
          const g = row.original;
          return (
            <div className="flex justify-end gap-1">
              {canRepair && <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); setRepairId(g.id); }}>Refazer pareamento</Button>}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleEnabled(g);
                }}
                title={g.enabled ? 'Pausar' : 'Reativar'}
              >
                {g.enabled ? (
                  <PauseCircle className="h-4 w-4 text-amber-600" />
                ) : (
                  <PlayCircle className="h-4 w-4 text-emerald-600" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setTargetVersionValue(g.targetVersion ?? '');
                  setDialog({ kind: 'target-version', gateway: g });
                }}
                title="Forçar versão"
              >
                <Wrench className="h-4 w-4 text-gray-600" />
              </Button>
              {canRepair && <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setDialog({ kind: 'delete', gateway: g });
                }}
                title="Desconectar gateway"
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </Button>}
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canRepair]
  );

  return (
    <DashboardLayout title="Orya Connector — Gateways">
      <div className="space-y-6">
        <div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatisticCard appearance="highlight" loading={overview.isLoading}
              title="Total gateways"
              value={overviewData.totalGateways}
              icon={Server}
              tone="primary"
            />
            <StatisticCard appearance="highlight" loading={overview.isLoading}
              title="Ativos"
              value={overviewData.activeGateways}
              icon={Activity}
              tone="success"
            />
            <StatisticCard appearance="highlight" loading={overview.isLoading}
              title="Offline"
              value={overviewData.offlineGateways}
              icon={Server}
              tone="warning"
            />
            <StatisticCard appearance="highlight" loading={overview.isLoading}
              title="DLQ pendente"
              value={overviewData.totalDlqPending}
              icon={AlertTriangle}
              tone={overviewData.totalDlqPending > 0 ? 'warning' : 'neutral'}
            />
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <strong>Falha ao carregar:</strong> {errorMessage}
          </div>
        ) : null}

        <ManagedDataTable<ManagerConnectorGateway, unknown>
          title="Lista de gateways"
          subtitle="Acompanhe os conectores, versões e disponibilidade por empresa."
          toolbarButtons={[
            { label: 'Atualizar', icon: <RefreshCw />, variant: 'subtle', onClick: () => { void overview.refetch(); void gateways.refetch(); } },
            { label: 'Releases', icon: <Download />, variant: 'subtle', onClick: () => setReleasesOpen(true) },
          ]}
          columnFilters={[
            { columnId: 'adapterId', label: 'Adapter', options: [...new Set((gateways.data ?? []).map((g) => g.adapterId))].sort().map((value) => ({ value, label: value })) },
            { columnId: 'releaseChannel', label: 'Canal', options: [...new Set((gateways.data ?? []).map((g) => g.releaseChannel))].sort().map((value) => ({ value, label: value })) },
            { columnId: 'effectiveStatus', label: 'Status', options: [...new Set((gateways.data ?? []).map((g) => g.effectiveStatus))].sort().map((value) => ({ value, label: value })) },
          ]}
          columns={columns}
          data={gateways.data ?? []}
          loading={gateways.isLoading}
          searchPlaceholder="Buscar por tenant, gateway, adapter…"
          emptyMessage="Nenhum gateway no parque."
          clientPagination
        />
      </div>

      <ConfirmDialog
        isOpen={dialog.kind === 'delete'}
        onClose={() => setDialog({ kind: 'none' })}
        onConfirm={confirmDelete}
        title="Desconectar gateway?"
        message={
          dialog.kind === 'delete'
            ? `A conexão de "${dialog.gateway.name}" será desativada e a credencial revogada. Clientes, vínculos, orçamentos e histórico serão preservados. Para conectar novamente, use Refazer pareamento.${operationError ? ` Erro: ${operationError}` : ''}`
            : ''
        }
        confirmText="Desconectar"
        variant="danger"
        isLoading={isSubmitting}
      />
      {repairId && <RepairPairingDialog gatewayId={repairId} onClose={() => setRepairId(null)} />}

      <Dialog
        isOpen={dialog.kind === 'target-version'}
        onClose={() => setDialog({ kind: 'none' })}
        title="Forçar versão do runtime"
        footer={dialog.kind === 'target-version' ? (
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialog({ kind: 'none' })} disabled={isSubmitting}>Cancelar</Button>
            <Button onClick={confirmTargetVersion} disabled={isSubmitting}>
              {isSubmitting ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        ) : undefined}
      >
        {dialog.kind === 'target-version' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Versão alvo para <strong>{dialog.gateway.name}</strong>. Vazio = libera auto-update no canal{' '}
              <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">
                {dialog.gateway.releaseChannel}
              </code>
              .
            </p>
            <Input
              value={targetVersionValue}
              onChange={(e) => setTargetVersionValue(e.target.value)}
              placeholder="ex: 0.2.1 (vazio = livre)"
              autoFocus
            />
          </div>
        ) : null}
      </Dialog>

      <InstallerReleasesModal
        open={releasesOpen}
        onClose={() => setReleasesOpen(false)}
      />
    </DashboardLayout>
  );
}
